import type { JSX } from "react";

import { TextTestID } from "@/constants/TestID";
import type { FAIconName, NavigationIconButtonProps } from "@/ui/core";
import { Icon, NavigationIconButton, Text } from "@/ui/core";
import { Box, HStack, StatusBarPadding } from "@/ui/layout";
import { ModalLayoutDimensions } from "@/ui/layout/ModalTypes";
import { ShadowStyles } from "@/ui/theme/styles";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface ModalTitleHeaderProps extends ViewWithTestID {
  actionComponents?: JSX.Element | null;
  icon?: FAIconName;
  modalAccessibilityTitle?: string;
  navigationType?: NavigationIconButtonProps["type"];
  onDismiss: () => void;
  title: string;
}

const ModalTitleHeader = ({
  actionComponents,
  icon,
  modalAccessibilityTitle,
  navigationType = "back",
  onDismiss,
  testID,
  title,
}: ModalTitleHeaderProps) => {
  const baseTestID = joinTestIDs(testID, "modal-header");

  return (
    <Box
      backgroundColor={ColorToken.NeutralBackgroundPrimary}
      testID={baseTestID}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...ShadowStyles}
    >
      {/*
        StatusBarPadding needs to be inside this shadowStyles Box for the padding to be within the shadowed borders of this header

        Additionally, no paddingHorizontal or paddingVertical can be applied to the outer Box containing StatusBarPadding,
        as StatusBarPadding will only render if it is at the very top of the of the screen, which vertical or horizontal padding will make untrue
      */}
      <StatusBarPadding />
      <HStack
        alignItems="center"
        height={ModalLayoutDimensions.HeaderHeight}
        justifyContent="space-between"
        paddingHorizontal={LayoutToken.ModalHeaderPadding}
        width="100%"
      >
        <HStack alignItems="center" flex={1} gap={12}>
          <NavigationIconButton
            accessibilityLabel={`Dismiss ${modalAccessibilityTitle}`}
            onPress={onDismiss}
            testID={baseTestID}
            type={navigationType}
          />
          <HStack alignItems="center" flex={1} gap={8}>
            {icon && <Icon name={icon} size="sm" testID={baseTestID} />}

            <Text
              numberOfLines={1}
              testID={joinTestIDs(baseTestID, TextTestID.Title)}
              variant="bodyMedium"
            >
              {title}
            </Text>
          </HStack>
        </HStack>
        <HStack alignItems="center" gap={12}>
          {actionComponents}
          {/* on web, show right-hand-side X to close for all "back" type modals ("close" type modals already have an X to close) */}
          {isWeb() && navigationType === "back" && (
            <NavigationIconButton
              accessibilityLabel={`Close ${modalAccessibilityTitle}`}
              onPress={onDismiss}
              testID={joinTestIDs(baseTestID, "close")}
              type="close"
            />
          )}
        </HStack>
      </HStack>
    </Box>
  );
};

export type { ModalTitleHeaderProps };
export default ModalTitleHeader;
